/**
 * Shared Beybling arena physics helpers (loaded before arena scripts).
 */
(function () {
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const rand = (min, max) => min + Math.random() * (max - min);

  /**
   * @param {HTMLElement} arena
   * @returns {{ width: number, height: number, rect: DOMRect }}
   */
  function measureArenaViewport(arena) {
    const rect = arena.getBoundingClientRect();
    let width = Math.max(rect.width, 1);
    let height = Math.max(rect.height, 1);

    if (width < 2 || height < 2) {
      const sideNav = getComputedStyle(document.documentElement)
        .getPropertyValue('--beybling-side-nav-width')
        .trim();
      const bottomNav = getComputedStyle(document.documentElement)
        .getPropertyValue('--beybling-bottom-nav-height')
        .trim();
      const sidePx = window.matchMedia('(min-width: 750px)').matches ? parseFloat(sideNav) || 220 : 0;
      const bottomPx = window.matchMedia('(max-width: 749px)').matches ? parseFloat(bottomNav) || 56 : 0;
      width = Math.max(document.documentElement.clientWidth - sidePx, 1);
      height = Math.max(window.innerHeight - bottomPx, 1);
    }

    return { width, height, rect };
  }

  /**
   * Playable bounds inside the arena, clamped to header bottom and bottom nav top.
   *
   * @param {HTMLElement} arena
   * @returns {{ width: number, height: number, ceiling: number, floor: number, leftWall: number }}
   */
  function measureArenaBounds(arena) {
    const { height, rect } = measureArenaViewport(arena);
    let ceiling = 0;
    let floor = height;
    let leftWall = 0;
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const viewportRight = document.documentElement.clientWidth;

    let playLeft = rect.left;
    let playRight = Math.min(rect.right, viewportRight);

    const sideNav = document.querySelector('.beybling-side-nav');
    if (isDesktop && sideNav) {
      const navStyle = getComputedStyle(sideNav);
      if (navStyle.display !== 'none' && navStyle.visibility !== 'hidden') {
        const navRight = sideNav.getBoundingClientRect().right;
        playLeft = Math.max(playLeft, navRight);
      }
    }

    leftWall = clamp(playLeft - rect.left, 0, Math.max(rect.width - 1, 0));
    playRight = Math.min(playRight, viewportRight);
    const playWidth = Math.max(playRight - rect.left - leftWall, 1);

    const header = document.getElementById('header-group');
    if (header) {
      const style = getComputedStyle(header);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        const headerRect = header.getBoundingClientRect();
        ceiling = clamp(headerRect.bottom - rect.top, 0, height);
      }
    }

    const bottomNav = document.querySelector('.beybling-bottom-nav');
    if (bottomNav) {
      const style = getComputedStyle(bottomNav);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        const navRect = bottomNav.getBoundingClientRect();
        floor = clamp(navRect.top - rect.top, ceiling + 1, height);
      }
    }

    return { width: playWidth, height, ceiling, floor, leftWall };
  }

  /**
   * Mobile tilt gravity for full-page arenas (contact gimmick).
   *
   * @param {{ strength?: number, smoothing?: number, onReady?: () => void }} [options]
   */
  function createArenaTiltGravity(options = {}) {
    const strength = options.strength ?? 0.42;
    const smoothing = options.smoothing ?? 0.16;
    const onReady = options.onReady;
    let gx = 0;
    let gy = strength;
    let targetGx = 0;
    let targetGy = strength;
    let active = false;
    let iosPermissionPending = false;
    /** @type {'motion' | 'orientation' | 'both' | null} */
    let mode = null;
    /** @type {((event: DeviceMotionEvent) => void) | null} */
    let motionHandler = null;
    /** @type {((event: DeviceOrientationEvent) => void) | null} */
    let orientationHandler = null;
    let lastMotionAt = 0;
    let useMotionOnly = false;

    function notifyReady() {
      if (typeof onReady === 'function') onReady();
    }

    function applyTiltVector(x, y) {
      const magnitude = Math.hypot(x, y);
      if (magnitude < 0.35) {
        targetGx = 0;
        targetGy = strength;
        return;
      }
      targetGx = clamp((x / magnitude) * strength, -strength, strength);
      targetGy = clamp((y / magnitude) * strength, -strength, strength);
    }

    function screenAngleRadians() {
      const angle = window.screen?.orientation?.angle ?? window.orientation ?? 0;
      return angle * (Math.PI / 180);
    }

    function setTargetFromMotion(event) {
      const g = event.accelerationIncludingGravity;
      if (!g || g.x == null || g.y == null) return;
      const angle = screenAngleRadians();
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const x = g.x * cos + g.y * sin;
      const y = -(g.x * sin + g.y * cos);
      lastMotionAt = performance.now();
      applyTiltVector(x, y);
    }

    function setTargetFromOrientation(event) {
      if (useMotionOnly) return;
      if (performance.now() - lastMotionAt < 150) return;

      const gamma = event.gamma;
      const beta = event.beta;
      if (gamma == null || Number.isNaN(gamma) || beta == null || Number.isNaN(beta)) return;

      const betaRad = (beta * Math.PI) / 180;
      const gammaRad = (gamma * Math.PI) / 180;
      const orientRad = screenAngleRadians();

      const deviceGx = Math.sin(gammaRad) * Math.cos(betaRad);
      const deviceGy = Math.sin(betaRad);
      const cos = Math.cos(orientRad);
      const sin = Math.sin(orientRad);
      const sx = deviceGx * cos + deviceGy * sin;
      const sy = deviceGx * sin + deviceGy * cos;
      applyTiltVector(sx, sy);
    }

    function startMotionListening() {
      if (!('DeviceMotionEvent' in window) || motionHandler) return false;
      motionHandler = setTargetFromMotion;
      window.addEventListener('devicemotion', motionHandler, { passive: true });
      return true;
    }

    function startOrientationListening() {
      if (!('DeviceOrientationEvent' in window) || orientationHandler) return false;
      orientationHandler = setTargetFromOrientation;
      window.addEventListener('deviceorientation', orientationHandler, { passive: true });
      window.addEventListener('deviceorientationabsolute', orientationHandler, { passive: true });
      return true;
    }

    function startListening() {
      if (active) return;

      const hasMotion = startMotionListening();
      const hasOrientation = hasMotion ? false : startOrientationListening();
      useMotionOnly = hasMotion;
      if (!hasMotion && !hasOrientation) return;

      active = true;
      mode = hasMotion && hasOrientation ? 'both' : hasMotion ? 'motion' : 'orientation';
      notifyReady();
    }

    function stopListening() {
      if (motionHandler) {
        window.removeEventListener('devicemotion', motionHandler);
        motionHandler = null;
      }
      if (orientationHandler) {
        window.removeEventListener('deviceorientation', orientationHandler);
        window.removeEventListener('deviceorientationabsolute', orientationHandler);
        orientationHandler = null;
      }
      mode = null;
      active = false;
      useMotionOnly = false;
      lastMotionAt = 0;
      gx = 0;
      gy = strength;
      targetGx = 0;
      targetGy = strength;
    }

    function requestIosPermissions(done) {
      const needOrientation =
        typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function';
      const needMotion =
        typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function';

      if (!needOrientation && !needMotion) {
        done(true);
        return;
      }

      const askMotion = function () {
        if (!needMotion) {
          done(true);
          return;
        }
        DeviceMotionEvent.requestPermission()
          .then(function (state) {
            done(state === 'granted');
          })
          .catch(function () {
            done(false);
          });
      };

      if (needOrientation) {
        DeviceOrientationEvent.requestPermission()
          .then(function (state) {
            if (state !== 'granted') {
              done(false);
              return;
            }
            askMotion();
          })
          .catch(function () {
            done(false);
          });
        return;
      }

      askMotion();
    }

    function requestOnGesture() {
      if (active || iosPermissionPending) return;

      const needsIosPermission =
        (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') ||
        (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function');

      if (!needsIosPermission) {
        startListening();
        return;
      }

      iosPermissionPending = true;

      requestIosPermissions(function (granted) {
        iosPermissionPending = false;
        if (granted) startListening();
      });
    }

    function tick() {
      gx += (targetGx - gx) * smoothing;
      gy += (targetGy - gy) * smoothing;
    }

    return {
      get gx() {
        return gx;
      },
      get gy() {
        return gy;
      },
      get active() {
        return active;
      },
      tick,
      startListening,
      stopListening,
      requestOnGesture,
    };
  }

  window.BeyblingArena = {
    REDUCED: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    MAX_BODIES_FULLPAGE_DESKTOP: 55,
    MAX_BODIES_FULLPAGE_MOBILE: 40,
    MAX_BODIES_BOXED: 24,
    DESKTOP_BODY_SCALE: 1.35,
    clamp,
    rand,
    measureArenaViewport,
    measureArenaBounds,
    createArenaTiltGravity,
  };
})();
